import React from 'react';
import { Link } from 'react-router-dom';
import { BackButton } from '../../components/ui';

const SellerPolicy = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white shadow rounded-lg p-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Seller Policy</h1>
            <BackButton
              onClick={() => window.history.back()}
              variant="link"
              size="small"
              color="#C97B53"
              ariaLabel="Back to Legal Center"
            />
          </div>

          <div className="prose max-w-none">
            <div className="mb-6">
              <p className="text-sm text-gray-500 mb-4">
                Effective Date: [Insert Date]
              </p>
              <p className="text-gray-700 mb-6">
                By opening a shop, listing a product, or offering a service on Vesta, you agree to this Seller Policy and all other terms, including our Terms of Service, Prohibited Items Policy, Payment & Fee Policy, and Content & Community Guidelines.
              </p>
              <p className="text-gray-700 mb-6">
                This policy is designed to ensure buyers have a safe, trustworthy experience and that sellers operate in compliance with applicable laws.
              </p>
            </div>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">1. What You Can Sell on Vesta</h2>
            
            <h3 className="text-lg font-semibold text-gray-900 mb-3">a. Eligible Listings</h3>
            <p className="text-gray-700 mb-4">You may sell:</p>
            <ul className="list-disc pl-6 mb-6 text-gray-700">
              <li className="mb-2"><strong>Food & Beverages</strong> – prepared under your direct control in compliance with applicable state cottage food laws or commercial food regulations.</li>
              <li className="mb-2"><strong>Made-to-Order Items</strong> – custom cakes, catering trays, seasonal items (with clear lead times).</li>
              <li className="mb-2"><strong>Chef or Catering Services</strong> – in-home or on-site cooking provided by you or your verified team members.</li>
              <li className="mb-2"><strong>Approved Packaged Foods</strong> – shelf-stable or properly packaged items that meet legal labeling standards.</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-900 mb-3">b. Original Work Requirement</h3>
            <p className="text-gray-700 mb-6">
              All items and services must be created, prepared, or provided by you (or your disclosed team). Reselling pre-made food from another business without disclosure is prohibited.
            </p>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">2. What You Can't Sell on Vesta</h2>
            <p className="text-gray-700 mb-4">Prohibited items include:</p>
            <ul className="list-disc pl-6 mb-6 text-gray-700">
              <li className="mb-2">Foods banned under your state's cottage/commercial food laws.</li>
              <li className="mb-2">Items requiring refrigeration or hot-holding that cannot be delivered/picked up within safe timeframes.</li>
              <li className="mb-2">Raw milk, uninspected meats, or prohibited game meats.</li>
              <li className="mb-2">Alcohol or cannabis/THC-infused products unless fully licensed and permitted.</li>
              <li className="mb-2">Products with unverified medical or health claims.</li>
              <li className="mb-2">Anything prohibited in our <Link to="/legal/prohibited-items" className="text-blue-600 hover:text-blue-500">Prohibited Items Policy</Link>.</li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">3. Compliance Requirements</h2>
            <p className="text-gray-700 mb-4">Sellers must:</p>
            <ul className="list-disc pl-6 mb-6 text-gray-700">
              <li className="mb-2">Obtain and maintain all required licenses, permits, and certifications for food preparation, catering, or retail sales.</li>
              <li className="mb-2">Upload proof of compliance (e.g., food handler card, business license, kitchen agreement) before publishing listings.</li>
              <li className="mb-2">Keep all credentials current; expired documents will result in listing suspension.</li>
              <li className="mb-2">Follow state labeling laws, including required cottage food disclaimers.</li>
              <li className="mb-2">Clearly state whether food was prepared in a home kitchen or commercial facility.</li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">4. Listing Standards</h2>
            <p className="text-gray-700 mb-4">Your listings must:</p>
            <ul className="list-disc pl-6 mb-6 text-gray-700">
              <li className="mb-2">Include clear, accurate descriptions of the product/service.</li>
              <li className="mb-2">Provide full ingredient lists and major allergen disclosures (per FDA and local rules).</li>
              <li className="mb-2">Use original photos/videos showing the actual product or service.</li>
              <li className="mb-2">Avoid misleading edits, filters, or AI-generated imagery unless disclosed.</li>
              <li className="mb-2">State the lead time for preorders or custom items.</li>
              <li className="mb-2">Specify delivery/pickup options, areas served, and service fees (if any).</li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">5. Video Content Rules</h2>
            <p className="text-gray-700 mb-4">Because Vesta is video-first:</p>
            <ul className="list-disc pl-6 mb-6 text-gray-700">
              <li className="mb-2">Show realistic preparation, presentation, or finished products.</li>
              <li className="mb-2">Do not depict unsafe food handling (cross-contamination, improper storage temps).</li>
              <li className="mb-2">No staged hazards for entertainment.</li>
              <li className="mb-2">Only use music, images, or footage you have the right to use.</li>
              <li className="mb-2">No hate speech, harassment, or discriminatory content.</li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">6. Order Fulfillment Standards</h2>
            
            <h3 className="text-lg font-semibold text-gray-900 mb-3">a. Delivery</h3>
            <ul className="list-disc pl-6 mb-4 text-gray-700">
              <li className="mb-2">Delivery must occur within safe timeframes for the product type.</li>
              <li className="mb-2">Use insulated packaging and temperature controls when required.</li>
              <li className="mb-2">Obtain proof of handoff (photo, signature, or confirmation code).</li>
              <li className="mb-2">Follow any special delivery instructions from the buyer that are reasonable and safe.</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-900 mb-3">b. Pickup</h3>
            <ul className="list-disc pl-6 mb-4 text-gray-700">
              <li className="mb-2">Have orders ready at the agreed time.</li>
              <li className="mb-2">Provide safe and hygienic pickup conditions.</li>
              <li className="mb-2">Hold orders for a reasonable grace period; uncollected orders may be discarded without refund.</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-900 mb-3">c. Catering & In-Home Services</h3>
            <ul className="list-disc pl-6 mb-6 text-gray-700">
              <li className="mb-2">Arrive on time with necessary equipment.</li>
              <li className="mb-2">Work in a sanitary, safe manner.</li>
              <li className="mb-2">Respect the buyer's property.</li>
              <li className="mb-2">Leave the premises clean and free of waste.</li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">7. Communication Standards</h2>
            <ul className="list-disc pl-6 mb-6 text-gray-700">
              <li className="mb-2">Respond to buyer inquiries within 24–48 hours.</li>
              <li className="mb-2">Keep buyers informed of delays or issues as soon as possible.</li>
              <li className="mb-2">Use Vesta's messaging tools for all order-related communication.</li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">8. Cancellations, Refunds & Disputes</h2>
            <ul className="list-disc pl-6 mb-6 text-gray-700">
              <li className="mb-2">Clearly state your cancellation and refund terms in your listings.</li>
              <li className="mb-2">For preorders, outline deposit requirements and refund cut-off dates.</li>
              <li className="mb-2">Attempt to resolve disputes directly with buyers before involving Vesta.</li>
              <li className="mb-2">Vesta may intervene if a resolution isn't reached within 48 hours.</li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">9. Feedback & Reviews</h2>
            <ul className="list-disc pl-6 mb-6 text-gray-700">
              <li className="mb-2">Buyers can leave reviews within the review window.</li>
              <li className="mb-2">You may publicly respond to reviews, but must remain professional.</li>
              <li className="mb-2">Review manipulation (incentivizing, suppressing, or posting fake reviews) is prohibited.</li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">10. Consequences for Policy Violations</h2>
            <p className="text-gray-700 mb-4">Violating this policy may result in:</p>
            <ul className="list-disc pl-6 mb-6 text-gray-700">
              <li className="mb-2">Listing removal.</li>
              <li className="mb-2">Temporary suspension of selling privileges.</li>
              <li className="mb-2">Permanent account termination.</li>
              <li className="mb-2">Withholding of funds if fraudulent activity is suspected.</li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">11. Policy Updates</h2>
            <p className="text-gray-700 mb-6">
              We may update this Seller Policy to reflect changes in law, industry standards, or Platform operations. Changes will be communicated via email or in-app notifications. Continued use of Vesta after changes constitutes acceptance.
            </p>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">12. Seller Checklist</h2>
            <div className="bg-green-50 p-4 rounded-lg mb-6">
              <h3 className="text-lg font-semibold text-green-900 mb-3">✅ Before You Start Selling:</h3>
              <ul className="list-disc pl-6 text-green-800">
                <li className="mb-1">Obtain required licenses and permits</li>
                <li className="mb-1">Upload compliance documentation</li>
                <li className="mb-1">Read all Vesta policies</li>
                <li className="mb-1">Set up secure payment processing</li>
                <li className="mb-1">Prepare clear listing descriptions</li>
              </ul>
            </div>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">13. Contact Information</h2>
            <div className="bg-gray-50 p-4 rounded-lg mb-6">
              <p className="text-gray-700">
                For questions about this policy or seller support:<br />
                <strong>Email:</strong> sellers@vesta.com<br />
                <strong>Support Hours:</strong> Monday-Friday, 9 AM - 6 PM EST
              </p>
            </div>

            <div className="mt-8 pt-6 border-t border-gray-200">
              <p className="text-sm text-gray-500">
                Last updated: {new Date().toLocaleDateString()}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SellerPolicy; 